import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import cx from "classnames";
import styles from "./ExpandableText.module.scss";

interface ExpandableTextProps {
  children: React.ReactNode;
  lines?: number;
}

export const ExpandableText: React.FC<ExpandableTextProps> = ({
  children,
  lines = 3,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCollapsable, setIsCollapsable] = useState(false);
  const [maxHeight, setMaxHeight] = useState<string | number>(0);

  const collapsedHeight = `${(lines * 1.1).toFixed(1)}rem`;

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }
    const el = ref.current;
    const fullHeight = el.scrollHeight;

    if (fullHeight > parseFloat(collapsedHeight) * 16) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsCollapsable(true);
      setMaxHeight(collapsedHeight);
    } else {
      setIsCollapsable(false);
      setMaxHeight(fullHeight);
    }
  }, [children, collapsedHeight]);

  const toggle = useCallback(() => {
    if (!isCollapsable || !ref.current) {
      return;
    }

    if (isExpanded) {
      setMaxHeight(collapsedHeight);
      setIsExpanded(false);
    } else {
      setMaxHeight(ref.current.scrollHeight);
      setIsExpanded(true);
    }
  }, [isExpanded, isCollapsable, collapsedHeight]);

  return (
    <div
      ref={ref}
      style={{
        maxHeight,
      }}
      className={cx(styles.text, {
        [styles.collapsed]: isCollapsable && !isExpanded,
      })}
      onClick={isCollapsable ? toggle : undefined}
    >
      {children}
    </div>
  );
};
